// Global notifications bell + dropdown (S48 follow-up — lifted out of the ops board into
// AppHeader so it appears on every authenticated page). Presentational only: it receives the
// shared notification state (useNotifications) and an onActivate(n) that the header implements
// with type-based routing (conflict/task → Ops Board, enquiry → /enquiries/:id) + mark-read.
//
// The former board-only "own-booking" interrupt popup (which needed ownTaskIds + form-open
// suppression) is intentionally removed in the globalisation — conflict notices still surface
// in this dropdown and deep-link to the board resolver.

import { useEffect, useRef, useState } from 'react';
import { C, F } from './theme';
import { useIsMobile } from '../../lib/useIsMobile';
import type { AppNotification } from '../../lib/conflicts';

interface Props {
  notifications: AppNotification[];
  unread: number;
  /** Route by type + mark read. Implemented by the global header. */
  onActivate: (n: AppNotification) => void;
}

function rowCta(n: AppNotification): string | null {
  if (n.enquiry_id) return 'Open enquiry →';
  if (n.conflict_item_id) return 'Open resolver →';
  if (n.task_id) return 'Open board →';
  return null;
}

export default function NotificationBell({ notifications, unread, onActivate }: Props) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on click-outside / Esc (the board version relied on a board click; a global bell needs both).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const activate = (n: AppNotification) => { onActivate(n); setOpen(false); };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        style={{ position: 'relative', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)', borderRadius: 5, padding: '6px 10px', fontSize: 13, cursor: 'pointer', fontFamily: F }}
      >
        🔔
        {unread > 0 && (
          <span style={{ position: 'absolute', top: -6, right: -6, background: C.red, color: '#fff', borderRadius: 9, minWidth: 16, height: 16, fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        // PR-000037: on a phone the absolute right-anchored 320px panel overflowed the LEFT edge
        // of the viewport. Mobile switches to a viewport-fitted fixed panel (left/right insets,
        // top measured from the bell so it clears the header wherever the bell sits). Desktop
        // keeps the original anchored dropdown, unchanged.
        <div style={isMobile
          ? { position: 'fixed', top: (wrapRef.current?.getBoundingClientRect().bottom ?? 56) + 6, left: 8, right: 8, width: 'auto', maxHeight: '70vh', overflowY: 'auto', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.22)', zIndex: 400, fontFamily: F }
          : { position: 'absolute', top: 36, right: 0, width: 320, maxHeight: 420, overflowY: 'auto', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.22)', zIndex: 400, fontFamily: F }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, color: C.text }}>Notifications</div>
          {notifications.length === 0 && <div style={{ padding: '14px 12px', fontSize: 11, color: C.textMuted }}>Nothing yet.</div>}
          {notifications.map((n) => {
            const cta = rowCta(n);
            return (
              <div key={n.id} onClick={() => activate(n)}
                style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: n.read_at ? C.surface : '#eff6ff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{n.title}</div>
                  {!n.read_at && <span style={{ width: 7, height: 7, borderRadius: 4, background: C.accent, flexShrink: 0, marginTop: 3 }} />}
                </div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2, lineHeight: 1.4 }}>{n.message}</div>
                {cta && <div style={{ fontSize: 10, color: C.accent, marginTop: 3, fontWeight: 700 }}>{cta}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
